import { useMemo, useState } from 'react';

import { Button, CheckBox, Dialog, EmptyState, ProgressButton } from '@/components/ui';

/**
 * Ein Auswahldialog für alle Zuordnungen der Administration (REQ-25 §4.4) — löst
 * `AdminFieldSelectionDialog`, `AdminGroupSelectionDialog` und `AdminViewSelectionDialog`
 * ab. Vorlage ist `PickerDialog.tsx` der ChinaApp: Kästchen statt Zeilenauswahl im Grid,
 * damit die Auswahl beim Suchen erhalten bleibt und ohne Maus bedienbar ist.
 */
export interface PickerOption<TId extends string | number = number> {
  id: TId;
  label: string;
  description?: string;
}

export interface PickerDialogProps<TId extends string | number = number> {
  open: boolean;
  title: string;
  options: PickerOption<TId>[];
  onClose: () => void;
  /** Mit den gewählten Ids; der Dialog schließt danach selbst. */
  onSubmit: (ids: TId[]) => void | Promise<void>;
  /** Text, wenn nichts mehr zur Auswahl steht. */
  emptyText?: string;
}

/**
 * Geschlossen rendert der Dialog nichts. Der Inhalt hängt darunter, damit Suche und Auswahl
 * bei jedem Öffnen frisch beginnen — eine Auswahl vom letzten Mal wäre eine Falle, weil die
 * gefilterte Liste sie nicht unbedingt zeigt.
 */
export function PickerDialog<TId extends string | number = number>(props: PickerDialogProps<TId>) {
  if (!props.open) return null;
  return <PickerDialogContent {...props} />;
}

function PickerDialogContent<TId extends string | number>(props: PickerDialogProps<TId>) {
  const { onClose, onSubmit, options } = props;
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState<ReadonlySet<TId>>(new Set());
  const [submitting, setSubmitting] = useState(false);

  const visible = useMemo(() => {
    const needle = search.trim().toLowerCase();
    if (needle === '') return options;
    return options.filter((option) =>
      `${option.label} ${option.description ?? ''}`.toLowerCase().includes(needle),
    );
  }, [options, search]);

  const toggle = (id: TId, checked: boolean) => {
    setSelected((current) => {
      const next = new Set(current);
      if (checked) next.add(id);
      else next.delete(id);
      return next;
    });
  };

  const submit = async () => {
    setSubmitting(true);
    try {
      // In der Reihenfolge der Liste, nicht in der Reihenfolge der Klicks.
      await onSubmit(
        options.filter((option) => selected.has(option.id)).map((option) => option.id),
      );
      onClose();
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog
      open
      width="480px"
      header={props.title}
      className="iwm-admin-picker"
      onClose={onClose}
      footer={
        <>
          <span className="iwm-admin-picker-count">{`${String(selected.size)} ausgewählt`}</span>
          <ProgressButton
            primary
            content="OK"
            progress={submitting}
            disabled={selected.size === 0}
            onClick={submit}
          />
          <Button content="Cancel" onClick={onClose} />
        </>
      }
    >
      {options.length === 0 ? (
        <EmptyState size="sm" title={props.emptyText ?? 'Nichts mehr zur Auswahl'} />
      ) : (
        <>
          <span className="e-input-group iwm-admin-search">
            <input
              type="search"
              className="e-input"
              placeholder="Suchen …"
              aria-label="Auswahl durchsuchen"
              value={search}
              onChange={(event) => {
                setSearch(event.target.value);
              }}
            />
            <span className="e-input-group-icon e-search-icon e-icons fa-solid fa-magnifying-glass" />
          </span>
          {visible.length === 0 ? (
            <EmptyState size="sm" title="Kein Treffer" />
          ) : (
            <ul className="iwm-admin-picker-list">
              {visible.map((option) => (
                <li key={option.id} className="iwm-admin-picker-item">
                  <CheckBox
                    label={option.label}
                    checked={selected.has(option.id)}
                    onChange={(checked) => {
                      toggle(option.id, checked);
                    }}
                  />
                  {option.description === undefined ? null : (
                    <span className="iwm-admin-picker-description">{option.description}</span>
                  )}
                </li>
              ))}
            </ul>
          )}
        </>
      )}
    </Dialog>
  );
}

export default PickerDialog;
